import { useState } from 'react';
import { Unterschrift } from './Unterschrift.jsx';
import { Icon } from '../components/Icon.jsx';
import { TeamWahl } from '../pages/Checkliste.jsx';
import { FotosFeld } from './Fotos.jsx';
import { load, save } from '../lib/storage.js';

/**
 * Eingaben für Formularfelder nach Typ – gross genug für Handschuhe (48 px), so wenige Tipps wie möglich.
 * Gleiche Bedienung wie die bisherige Übersichtsliste.
 */

/** Häkchen als Knopf (breit = füllt die Rasterzelle). */
export function Haken({ an, label, onClick, breit, schmal }) {
  return (
    <button type="button" className={`chip${an ? ' on' : ''}`} aria-pressed={an} onClick={onClick}
      style={{ height: 48, padding: schmal ? 0 : '0 6px', fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        minWidth: breit ? 0 : 92, width: breit ? '100%' : schmal ? 72 : undefined, flexShrink: 0 }}>
      {an && !breit && !schmal && <Icon name="check" size={13} stroke={3} />}{label}
    </button>
  );
}

/** Zuletzt verwendete Werte einer Auswahl (auf dem Gerät, neueste zuerst). */
export const gemerkt = (f) => (f.merken ? load(f.merken, []) : []);
export function merken(f, wert) {
  if (!f.merken || !wert) return;
  save(f.merken, [wert, ...gemerkt(f).filter((x) => x !== wert)].slice(0, 8));
}


/**
 * Ein Feld (ohne Häkchen – die zeigt die Zeile selbst).
 * @param {{ f: object, wert: unknown, onChange: (v: unknown) => void, team: string[], vorschlaege?: string[], aria?: string }} p
 */
export function Feld({ f, wert, onChange, team, vorschlaege, aria }) {
  const label = aria ?? f.titel;
  if (f.typ === 'person') {
    return (
      <div className="stack" style={{ gap: 6 }}>
        <span className="feldlabel">{f.titel}</span>
        <TeamWahl wert={wert} team={team} onChange={onChange} />
      </div>
    );
  }
  if (f.typ === 'auswahl') return <Auswahl f={f} wert={wert} onChange={onChange} vorschlaege={vorschlaege ?? f.optionen ?? []} aria={label} />;
  if (f.typ === 'mehrfach') return <Mehrfach f={f} wert={Array.isArray(wert) ? wert : []} onChange={onChange} aria={label} />;
  if (f.typ === 'fotos') return <FotosFeld f={f} wert={wert} onChange={onChange} />;
  if (f.typ === 'unterschrift') {
    return (
      <div className="stack" style={{ gap: 6 }}>
        <span className="feldlabel">{f.titel}</span>
        <Unterschrift wert={wert} onChange={onChange} label={label} />
      </div>
    );
  }
  if (f.typ === 'haken') return <div style={{ maxWidth: 140 }}><Haken breit an={wert === true} label={f.titel} onClick={() => onChange(!wert)} /></div>;
  const eingabe = {
    datum: { type: 'date', max: '2099-12-31', style: { height: 48, padding: '0 8px', fontSize: 15 } },
    zeit: { type: 'time', style: { height: 48, padding: '0 8px', fontSize: 15 } },
    zahl: { inputMode: 'decimal', style: { height: 48 } },
  }[f.typ] ?? { style: { height: 48 } };
  return (
    <label className="stack" style={{ gap: 6, maxWidth: f.typ === 'datum' || f.typ === 'zeit' ? 190 : undefined }}>
      <span className="feldlabel">{f.titel}</span>
      {f.typ === 'text'
        ? <textarea className="input" rows={3} value={wert ?? ''} maxLength={f.max} placeholder={f.platzhalter} onChange={(e) => onChange(e.target.value)} aria-label={label} />
        : <input className="input" {...eingabe} value={wert ?? ''} maxLength={f.max} placeholder={f.platzhalter} onChange={(e) => onChange(e.target.value)} aria-label={label} />}
    </label>
  );
}

/**
 * Auswahl: Chips (Tipp auf den gewählten = abwählen) und «Eigenes …» zum Eintippen (`frei`).
 * Darstellung «feld»: Textfeld mit Vorschlägen darunter (z. B. Notiz).
 */
function Auswahl({ f, wert, onChange, vorschlaege, aria }) {
  const eigenes = !!wert && !vorschlaege.includes(wert);
  const [andere, setAndere] = useState(false);
  const waehle = (v) => { const neu = wert === v ? '' : v; onChange(neu); merken(f, neu); };
  if (f.darstellung === 'feld') {
    return (
      <div className="stack" style={{ gap: 6 }}>
        <label className="stack" style={{ gap: 6 }}>
          <span className="feldlabel">{f.titel}</span>
          <input className="input" style={{ height: 48 }} value={wert ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={f.platzhalter} maxLength={f.max} aria-label={aria} />
        </label>
        <div className="wrapchips">
          {vorschlaege.map((o) => <button key={o} type="button" className={`chip${wert === o ? ' on' : ''}`} style={{ height: 48, padding: '0 14px', fontSize: 14 }} onClick={() => waehle(o)}>{o}</button>)}
        </div>
      </div>
    );
  }
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="feldlabel">{f.titel}</span>
      <div className="wrapchips" aria-label={aria}>
        {vorschlaege.map((o) => (
          <button key={o} type="button" className={`chip accent${wert === o ? ' on' : ''}`} style={{ height: 48, padding: '0 14px', fontSize: 14 }} onClick={() => waehle(o)}>{o}</button>
        ))}
        {f.frei && (
          <button type="button" className={`chip accent${eigenes || andere ? ' on' : ''}`} style={{ height: 48, padding: '0 14px', fontSize: 14 }} onClick={() => setAndere((x) => !x)}>
            {eigenes ? `✎ ${wert}` : 'Eigenes …'}
          </button>
        )}
      </div>
      {andere && (
        <input className="input" style={{ height: 48 }} autoFocus placeholder={f.platzhalter ?? 'Eintippen'} maxLength={f.max} defaultValue={eigenes ? wert : ''}
          onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== wert) { onChange(v); merken(f, v); } setAndere(false); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
      )}
    </div>
  );
}

/**
 * Mehrfachauswahl: Chips an/aus (Optionen, zuletzt verwendete, eigene), `frei` = «Andere …» zum Eintippen.
 * Reihenfolge bleibt die der Optionen, eigene Werte dahinter.
 */
function Mehrfach({ f, wert, onChange, aria }) {
  const [andere, setAndere] = useState(false);
  const optionen = [...new Set([...(f.optionen ?? []), ...gemerkt(f), ...wert])];
  const umschalten = (o) => {
    const neu = wert.includes(o) ? wert.filter((x) => x !== o) : optionen.filter((x) => x === o || wert.includes(x));
    onChange(neu);
    if (!wert.includes(o)) merken(f, o);
  };
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="feldlabel">{f.titel}</span>
      <div className="wrapchips" role="group" aria-label={aria}>
        {optionen.map((o) => (
          <button key={o} type="button" className={`chip accent${wert.includes(o) ? ' on' : ''}`} aria-pressed={wert.includes(o)} style={{ height: 48, padding: '0 14px', fontSize: 14 }} onClick={() => umschalten(o)}>{o}</button>
        ))}
        {f.frei && <button type="button" className={`chip${andere ? ' on' : ''}`} style={{ height: 48, padding: '0 14px', fontSize: 14 }} onClick={() => setAndere((x) => !x)}>Andere …</button>}
      </div>
      {andere && (
        <input className="input" style={{ height: 48 }} autoFocus placeholder={f.platzhalter ?? 'Eintippen'} maxLength={f.max ?? 80}
          onBlur={(e) => { const t = e.target.value.trim(); if (t && !wert.includes(t)) { onChange([...wert, t]); merken(f, t); } setAndere(false); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
      )}
    </div>
  );
}

/** Aufklapp-Zeile: graue Kurzinfo, Tipp zeigt/verbirgt die Details. */
export function Kurzinfo({ text, offen, onClick, leer }) {
  return (
    <button type="button" onClick={onClick} aria-expanded={offen}
      style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: text ? 'var(--ink-2)' : 'var(--muted)', minHeight: 48 }}>
      <span className="ellipsis" style={{ flex: 1, minWidth: 0 }}>{text || leer}</span>
      <span style={{ display: 'inline-flex', transform: `rotate(${offen ? -90 : 90}deg)`, color: 'var(--muted)' }}><Icon name="chevron" size={16} /></span>
    </button>
  );
}
